import C from "@src/Controller"
import { createFilename } from "@src/filepath"
import { ISubscriptionIdState, tBrandState } from "@src/Model/Model"
import { isAfter, parseISO, startOfDay } from "date-fns"
import * as _ from "lodash"
import { Store } from "redux"

// Polyfill for __filename in browser environment
const __filename = createFilename("ControllerModules/Plans.ts")

import { ChannelObject } from "@zeguro/schema-validator/dist/types/coco/saasPlan/combined"

import * as PLANS from "../Actions/PlansActions"
import {
  actions as BRAND,
  initialBrandState,
} from "../Actions/Reducers/BrandReducer"
import { actions as SUBSCRIPTIONID } from "../Actions/Reducers/SubscriptionIdReducer"
import { serverLog } from "./Logging"
import * as Middletier from "./Middletier"

class Plans {
  private store: Store

  constructor(store: Store) {
    this.store = store
  }

  getFeatureUsageProfile(
    callback?: (success: boolean, error?: string) => void,
  ) {
    this.store.dispatch(PLANS.setSaasPlansStatus("LOADING"))
    this.store.dispatch(BRAND.getBrandRequest())
    Middletier.fetchJson(`/plans/featureUsageProfile`, (success, data) => {
      if (success) {
        if (data.success) {
          // eslint-disable-next-line prefer-const
          let { brand, ...plans } = data.data.plans
          const trialExpired =
            isAfter(
              startOfDay(new Date()),
              startOfDay(parseISO(plans.expirationDate)),
            ) && plans.subscriptionStatus === "INTRIAL"
          const modules = {
            compliance: plans.securityPolicies,
            ratings: plans.bitsight,
            cybermaturity: plans.cybermaturity,
            training: plans.training,
            monitoring: plans.probely,
            insurance: plans.insurance,
            dashboard:
              plans.cybermaturity === "ACTIVE" ||
              plans.training === "ACTIVE" ||
              plans.probely === "ACTIVE" ||
              plans.securityPolicies === "ACTIVE"
                ? "ACTIVE"
                : "NOT_AVAILABLE",
            marketplace: plans.marketplace,
          }
          if (trialExpired) {
            if (modules.compliance === "ACTIVE") {
              modules.compliance = "INACTIVE"
            }
            if (modules.training === "ACTIVE") {
              modules.training = "INACTIVE"
            }
            if (modules.monitoring === "ACTIVE") {
              modules.monitoring = "INACTIVE"
            }
            if (modules.cybermaturity === "ACTIVE") {
              modules.cybermaturity = "INACTIVE"
            }
          }

          const isHighestPlan =
            _.lowerCase(
              plans.channelObject.eligiblePlans[
                plans.channelObject.eligiblePlans.length - 1
              ]?.planPointer?.appId?.planId,
            ) === _.lowerCase(plans.subscribedPlanPointer?.appId?.planId)

          plans = { ...plans, modules, isHighestPlan }
          this.store.dispatch(
            SUBSCRIPTIONID.updateSubscriptionId({
              ...this.store.getState().subscriptionId,
              channelId: plans.channel.appId.channelId,
              planId: plans.subscribedPlanPointer.appId.planId,
              subscriptionId: plans.subscriptionId,
              availableLocales: plans.channelObject.availableLocales,
              defaultUILocale: plans.channelObject.defaultUILocale,
            }),
          )

          this.store.dispatch(PLANS.setSaasPlans(plans))
          this.store.dispatch(PLANS.setSaasPlansStatus("SUCCESS"))
          this.store.dispatch(BRAND.getBrandSuccess(brand))
          if (callback) {
            callback(true)
          }
        } else {
          this.store.dispatch(PLANS.setSaasPlansStatus("FAILED"))
          if (callback) {
            callback(false)
          }
        }
      } else {
        this.store.dispatch(PLANS.setSaasPlansStatus("FAILED"))
        this.store.dispatch(
          BRAND.getBrandFailure(Error("Cannot fetch saas plans")),
        )

        if (callback) {
          callback(false, "Cannot fetch saas plans")
        }

        serverLog({
          level: "error",
          message: "Cannot fetch saas plans",
          filename: __filename,
        })
      }
    })
  }

  getEligiblePlans() {
    this.store.dispatch(PLANS.setEligiblePlansStatus("LOADING"))
    try {
      Middletier.fetchJson(`/plans/getEligiblePlans`, (success, data) => {
        if (success) {
          if (data.success) {
            const eligiblePlans = data.data.eligiblePlans
            const hasChargebeeSubscription = data.data.hasSubscription
            this.store.dispatch(PLANS.setEligiblePlans(eligiblePlans))
            this.store.dispatch(
              PLANS.setChargebeeSubscriptionStatus(hasChargebeeSubscription),
            )
            this.store.dispatch(PLANS.setEligiblePlansStatus("SUCCESS"))
          } else {
            throw Error("Unable to fetch eligible plans")
          }
        } else {
          throw Error("Unable to fetch eligible plans")
        }
      })
    } catch {
      this.store.dispatch(PLANS.setEligiblePlansStatus("FAILED"))
      serverLog({
        level: "error",
        message: "Unable to fetch eligible plans",
        filename: __filename,
      })
    }
  }

  subscribePlan(planId?: string, callback?: any) {
    const state = this.store.getState()
    Middletier.xhrPost(
      "/plans/subscribePlan",
      JSON.stringify({
        planId,
      }),
      (response: any) => {
        if (callback) {
          callback({ success: true })
        }
        if (response.success) {
          C.toastNotification("Plan is successfully upgraded", "success")
          C.Portal.sendMarketingAnalytics(
            {
              email: state.portal.userProfile && state.portal.userProfile.email,
              plan: planId,
              security_subscription_status: "ACTIVE",
            },
            window.location.href,
            "Cybersafty Subscription Change",
          )
          this.getFeatureUsageProfile()
          this.getEligiblePlans()
        } else {
          const error = response.error
          C.toastNotification("Upgrade plan failed", "danger")
          serverLog({
            level: "error",
            message: "Errror in setting subscription",
            filename: __filename,
            stack: error,
          })
        }
      },
    )
  }

  isValidSubscriptionId(
    subscriptionId?: ISubscriptionIdState["subscriptionId"],
    callback?: (
      state: "expired" | "invalid" | "valid" | "failed" | "ready",
      uiRules: ISubscriptionIdState["uiRules"],
      channelId: ISubscriptionIdState["channelId"],
      planId: ISubscriptionIdState["planId"],
      subscriptionId: ISubscriptionIdState["subscriptionId"],
      brand: tBrandState,
      availableLocales: ChannelObject["availableLocales"],
      defaultUILocale: ChannelObject["defaultUILocale"] | null,
    ) => void,
  ) {
    try {
      this.store.dispatch(
        SUBSCRIPTIONID.updateSubscriptionId({
          subscriptionIdState: "loading",
          uiRules: {},
          channelId: "",
          planId: "",
          subscriptionId: "",
          availableLocales: [],
          defaultUILocale: null,
        }),
      )
      this.store.dispatch(BRAND.getBrandRequest())
      Middletier.fetchJson(
        `/plans/isValidSubscriptionId/${subscriptionId || ""}`,
        (success, response) => {
          if (success) {
            const status = response.data.subscriptionIdStatus
            const uiRules = response.data.uiRules || {}
            const channelId = response.data.channelId
            const planId = response.data.planId
            const subscriptionId = response.data.subscriptionId
            const brand = response.data.brand
            const availableLocales = response.data.availableLocales
            const defaultUILocale = response.data.defaultUILocale

            this.store.dispatch(
              SUBSCRIPTIONID.updateSubscriptionId({
                subscriptionIdState: status,
                uiRules,
                channelId,
                planId,
                subscriptionId,
                availableLocales,
                defaultUILocale,
              }),
            )
            this.store.dispatch(BRAND.getBrandSuccess(brand))

            if (callback) {
              try {
                callback(
                  status,
                  uiRules,
                  channelId,
                  planId,
                  subscriptionId,
                  brand,
                  availableLocales,
                  defaultUILocale,
                )
              } catch (callbackError) {
                console.error("Callback error", callbackError)
              }
            }
          } else {
            this.store.dispatch(
              SUBSCRIPTIONID.updateSubscriptionId({
                subscriptionIdState: "failed",
                uiRules: {},
                channelId: "",
                planId: "",
                subscriptionId: "",
                availableLocales: [],
                defaultUILocale: null,
              }),
            )
            this.store.dispatch(BRAND.getBrandFailure(response.error))

            throw Error("Cannot validate subscription Id")
          }
        },
      )
    } catch {
      if (callback) {
        callback("failed", {}, "", "", "", initialBrandState, [], null)
      }
      this.store.dispatch(
        SUBSCRIPTIONID.updateSubscriptionId({
          subscriptionIdState: "failed",
          uiRules: {},
          channelId: "",
          planId: "",
          subscriptionId: "",
          availableLocales: [],
          defaultUILocale: null,
        }),
      )
      this.store.dispatch(
        BRAND.getBrandFailure(Error("Cannot validate subscription Id")),
      )
      serverLog({
        level: "error",
        message: "Cannot validate subscription Id",
        filename: __filename,
      })
    }
  }

  updatePlan(planId: string) {
    const state = this.store.getState()
    this.store.dispatch(PLANS.setUpdatePlanStatus("LOADING"))
    Middletier.xhrPost(
      "/plans/updatePlan",
      JSON.stringify({
        planId,
      }),
      (response: any) => {
        if (response.success) {
          C.Portal.sendMarketingAnalytics(
            {
              email: state.portal.userProfile && state.portal.userProfile.email,
              plan: planId,
              security_subscription_status: "ACTIVE",
            },
            window.location.href,
            "Cybersafty Subscription Change",
          )
          this.getFeatureUsageProfile()
          this.getEligiblePlans()
          this.store.dispatch(PLANS.setUpdatePlanStatus("SUCCESS"))
        } else {
          const error = response.error
          this.store.dispatch(PLANS.setUpdatePlanStatus("FAILED"))
          serverLog({
            level: "error",
            message: "Errror in setting subscription",
            filename: __filename,
            stack: error,
          })
        }
      },
    )
  }
}

export default Plans
